"use client";
import React, { useState, useTransition } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  IconBrandTelegram,
  IconWorld,
  IconBrandTwitterFilled,
  IconBrandDiscordFilled,
  IconFlameFilled,
  IconHelp,
  IconLoader,
} from "@tabler/icons-react";
import Input from "@/components/ui/input/input";
import { Button } from "@/components/ui/button/button";
import ImageUpload from "../ui/input/fileUpload";
import TokenSuccessModal from "../ui/modal/tokenSuccessModal";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";
import { LAMPORTS_PER_SOL, Transaction } from "@solana/web3.js";
import { prepareLaunchTransaction } from "@/utils/tokenUtils";

interface TokenLaunchFormData extends FormData {
  get(key: "image"): File;
  get(key: "name"): string;
  get(key: "symbol"): string;
  get(key: "buyAmount"): string;
  get(key: "description"): string;
  get(key: "telegram" | "twitter" | "discord" | "website"): string | null;
}
const schema = yup.object({
  tokenName: yup
    .string()
    .required(
      "Token Name is required. Please enter a valid name for your token."
    ),
  tokenSymbol: yup
    .string()
    .matches(
      /^[A-Z0-9]+$/,
      "Please enter a valid symbol (uppercase letters and numbers only)."
    )
    .required("Token Symbol is required."),
  description: yup
    .string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(500, "Description must not exceed 500 characters"),
  buyAmount: yup
    .number()
    .required("Buy Amount is required.")
    .min(0.001, "Buy Amount must be at least 0.001 SOL"),
  telegram: yup
    .string()
    .url(
      "Please enter a valid Telegram group link (e.g., https://t.me/yourgroup)"
    )
    .notRequired(),
  twitter: yup
    .string()
    .url(
      "Please enter a valid Twitter handle or link (e.g., https://twitter.com/yourhandle)"
    )
    .notRequired(),
  discord: yup
    .string()
    .url(
      "Please enter a valid Discord invite link (e.g., https://discord.gg/yourlink)"
    )
    .notRequired(),
  website: yup
    .string()
    .url("Please enter a valid website URL (e.g., https://www.yourwebsite.com)")
    .notRequired(),
  image: yup
    .mixed<FileList>()
    .test("required", "Please upload a valid image file.", (value) => {
      // Check if the value exists and has files
      return (
        value !== undefined && value.length > 0 && value instanceof FileList
      );
    }),
});

type FormValues = yup.InferType<typeof schema>;

const TokenLaunchForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const [previewImage, setPreviewImage] = React.useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const { connected, publicKey, signTransaction } = useWallet();
  const [isPending, startTransaction] = useTransition();
  const { connection } = useConnection();
  const [tokenMint, setTokenMint] = useState<string | null>(null);
  const currentImage = watch("image");

  // Make sure the preview image is updated when the image changes
  React.useEffect(() => {
    if (currentImage && currentImage.length > 0 && !previewImage) {
      const file = currentImage[0];
      if (file && file instanceof File) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImage(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  }, [currentImage, previewImage]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!connected || !publicKey) {
      toast.error("Please connect your wallet");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const {
      tokenName,
      tokenSymbol,
      buyAmount,
      description,
      telegram,
      twitter,
      discord,
      website,
      image,
    } = data;
    const lamports = Math.floor(buyAmount * LAMPORTS_PER_SOL);
    const formData = new FormData() as TokenLaunchFormData;
    if (image) {
      formData.append("image", image[0] as File);
    }
    formData.append("name", tokenName);
    formData.append("symbol", tokenSymbol);
    formData.append("buyAmount", lamports.toString());
    formData.append("description", description);
    formData.append("publicKey", publicKey.toBase58());
    telegram && formData.append("telegram", telegram);
    twitter && formData.append("twitter", twitter);
    discord && formData.append("discord", discord);
    website && formData.append("website", website);

    startTransaction(async () => {
      try {
        toast.loading("Launching token...", { id: "create" });
        // 1. Prepare the transaction
        const response = await prepareLaunchTransaction(formData);
        if (!response.success) {
          throw new Error(response.error || "Failed to prepare transaction");
        }
        const { serializedTransaction, tokenMint } = response.data;
        setTokenMint(tokenMint?.publicKey);

        // 2. Deserialize the transaction
        const transaction = Transaction.from(
          Buffer.from(serializedTransaction, "base64")
        );
        // 2. Sign the transaction with the user's wallet
        if (!signTransaction) {
          throw new Error("Wallet does not support transaction signing!");
        }
        const signedTransaction = await signTransaction(transaction);
        // 3. Send the transaction to the network
        const signature = await connection.sendRawTransaction(
          signedTransaction.serialize(),
          { maxRetries: 5 }
        );
        // 4. Wait for confirmation
        const { blockhash, lastValidBlockHeight } =
          await connection.getLatestBlockhash("finalized");
        await connection.confirmTransaction(
          {
            blockhash,
            lastValidBlockHeight,
            signature,
          },
          "confirmed"
        );
        toast.success("Token launched successfully", { id: "create" });
        setOpen(true);
        reset();
        setPreviewImage(null);
      } catch (error) {
        console.log(error);
        toast.error("Failed to create token", { id: "create" });
        toast.dismiss("create");
      }
    });
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full mt-16 mx-auto p-8 space-y-6 border-2 border-primaryborder bg-background rounded-2xl"
      >
        <h2 className="text-white text-xl font-semibold mb-2">
          Launch Your Token in One Step
        </h2>
        <p className="text-muted mb-6 text-offwhite">
          Quickly deploy your token to the blockchain with minimal setup. Fill
          in the essential details and launch in an instant.
        </p>

        {/* Image Upload */}
        <ImageUpload
          value={watch("image")}
          error={errors.image?.message}
          onChange={(files) => {
            setValue("image", files as FileList, { shouldValidate: true });
          }}
          previewUrl={previewImage}
          setPreviewUrl={setPreviewImage}
        />

        <Input
          label="Token Name"
          placeholder="Enter your token's name (e.g., MyToken)"
          {...register("tokenName")}
          error={errors.tokenName?.message}
          endIcon={<IconHelp size={16} stroke={2} />}
        />

        <Input
          label="Token Symbol"
          placeholder="Enter the token symbol (e.g., MT)"
          {...register("tokenSymbol")}
          error={errors.tokenSymbol?.message}
          endIcon={<IconHelp size={16} stroke={2} />}
        />

        <div className="mb-4">
          <label className="text-sm text-muted font-medium block mb-1">
            Description
          </label>
          <div className="relative">
            <textarea
              {...register("description")}
              className={cn(
                "w-full min-h-[120px] border border-primaryborder bg-input-bgcolor rounded-md px-4 py-3 text-offwhite resize-none",
                errors.description && "border-red-500"
              )}
              placeholder="Enter your token's description..."
            />
            <div className="absolute right-3 top-3">
              <IconHelp size={16} stroke={2} className="text-muted" />
            </div>
          </div>
          {errors.description && (
            <p className="text-red-500 text-sm mt-1">
              {String(errors.description.message)}
            </p>
          )}
        </div>

        <Input
          label="Buy Amount (SOL)"
          placeholder="Enter the buy amount (e.g., 0.01)"
          {...register("buyAmount")}
          error={errors.buyAmount?.message}
          endIcon={<IconHelp size={16} stroke={2} />}
          value={watch("buyAmount") || 0.001}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            placeholder="Enter Telegram group link (optional)"
            icon={<IconBrandTelegram size={18} />}
            {...register("telegram")}
            error={errors.telegram?.message}
          />
          <Input
            placeholder="Enter Twitter handle (optional)"
            icon={<IconBrandTwitterFilled size={18} />}
            {...register("twitter")}
            error={errors.twitter?.message}
          />
          <Input
            placeholder="Enter Discord invite link (optional)"
            icon={<IconBrandDiscordFilled size={18} />}
            {...register("discord")}
            error={errors.discord?.message}
          />
          <Input
            placeholder="Enter your website link (optional)"
            icon={<IconWorld size={18} />}
            {...register("website")}
            error={errors.website?.message}
          />
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            type="submit"
            className="px-6 bg-red-500 rounded-full text-white"
            disabled={isPending}
          >
            {isPending ? (
              <IconLoader size={16} className="animate-spin" />
            ) : (
              <IconFlameFilled size={16} />
            )}{" "}
            {isPending ? "Launching..." : "Launch Now"}
          </Button>
        </div>
      </form>
      <TokenSuccessModal
        open={open}
        onOpenChange={setOpen}
        tokenAddress={tokenMint}
      />
    </>
  );
};

export default TokenLaunchForm;
